// components/Admin/MiniroomManager/MiniroomFurnitureTable.jsx



import React, { useState, useMemo } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Chip, TextField, Box, Pagination, Stack, Select, MenuItem, Typography, Dialog,
    DialogTitle, DialogContent, DialogActions, Button, Checkbox, FormControlLabel
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import api from "../../../api/axios";

const fullUrl = (url) => (url?.startsWith("http") ? url : `${window.location.origin}${url ?? ""}`);

export default function MiniroomFurnitureTable({ items, refresh }) {
    const PER_PAGE = 8;
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all");
    const [editItem, setEditItem] = useState(null);
    const [newImage, setNewImage] = useState(null);
    const [newBackImage, setNewBackImage] = useState(null);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return items
            .filter(i => (filter === "active" ? i.is_active : filter === "inactive" ? !i.is_active : true))
            .filter(i => !q || i.name?.toLowerCase().includes(q) || i.category?.toLowerCase().includes(q));
    }, [items, search, filter]);

    const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

    const toggleActive = async (id, current) => {
        await api.patch(`/api/miniroom/admin/furnitures/${id}/`, { is_active: !current });
        refresh();
    };

    const remove = async (id) => {
        if (!window.confirm("삭제하시겠습니까?")) return;
        await api.delete(`/api/miniroom/admin/furnitures/${id}/`);
        refresh();
    };

    const saveEdit = async () => {
        const fd = new FormData();
        ["name", "size_w", "size_h", "anchor_x", "anchor_y", "layer", "category"].forEach(k => {
            if (editItem[k] !== undefined && editItem[k] !== null) fd.append(k, editItem[k]);
        });
        fd.append("rotatable", (!!editItem.rotatable).toString());
        fd.append("is_active", (!!editItem.is_active).toString());
        fd.append("price", Number(editItem.price ?? 0));
        fd.append("is_unlimited", (!!editItem.is_unlimited).toString());
        fd.append("stock", (!!editItem.is_unlimited) ? 0 : Number(editItem.stock ?? 0));
        if (newImage) fd.append("sprite", newImage);
        if (newBackImage) fd.append("sprite_back", newBackImage);

        await api.patch(`/api/miniroom/admin/furnitures/${editItem.id}/`, fd, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        setEditItem(null);
        setNewImage(null);
        setNewBackImage(null);
        refresh();
    };

    return (
        <>
            <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap">
                <TextField
                    size="small"
                    label="이름/카테고리 검색"
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                />
                <Select size="small" value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}>
                    <MenuItem value="all">전체</MenuItem>
                    <MenuItem value="active">노출중</MenuItem>
                    <MenuItem value="inactive">숨김</MenuItem>
                </Select>
                <Typography variant="body2" sx={{ ml: "auto" }}>
                    총 {filtered.length}개
                </Typography>
            </Stack>

            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            <TableCell>앞 미리보기</TableCell>
                            <TableCell>등 미리보기</TableCell>
                            <TableCell>이름</TableCell>
                            <TableCell>카테고리</TableCell>
                            <TableCell>레이어</TableCell>
                            <TableCell>크기(W×H)</TableCell>
                            <TableCell>회전</TableCell>
                            <TableCell>가격</TableCell>
                            <TableCell>판매</TableCell> {/* 무제한/재고 */}
                            <TableCell>상태</TableCell>
                            <TableCell align="right">관리</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paged.map(i => (
                            <TableRow key={i.id}>
                                <TableCell align="center">
                                    {i.sprite_url ? (
                                        <img src={fullUrl(i.sprite_url)} alt={i.name} width={48} style={{ objectFit: "contain" }} />
                                    ) : "—"}
                                </TableCell>
                                <TableCell align="center">
                                    {i.back_sprite_url ? (
                                        <img src={fullUrl(i.back_sprite_url)} alt={`${i.name} back`} width={48} style={{ objectFit: "contain" }} />
                                    ) : "—"}
                                </TableCell>
                                <TableCell>{i.name}</TableCell>
                                <TableCell>{i.category || "—"}</TableCell>
                                <TableCell>{i.layer}</TableCell>
                                <TableCell>{i.size_w}×{i.size_h}</TableCell>
                                <TableCell>{i.rotatable ? "가능" : "불가"}</TableCell>
                                <TableCell>{(i.price ?? 0).toLocaleString()} Leaf🌿</TableCell>
                                <TableCell>
                                    {i.is_unlimited ? "무제한" : `재고: ${i.stock ?? 0}개`}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={i.is_active ? "노출중" : "숨김"}
                                        color={i.is_active ? "success" : "default"}
                                        icon={i.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                        onClick={() => toggleActive(i.id, i.is_active)}
                                        clickable
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => setEditItem({ ...i })}><EditIcon /></IconButton>
                                    <IconButton onClick={() => remove(i.id)}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {paged.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>데이터가 없습니다.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {filtered.length > PER_PAGE && (
                <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                        count={Math.ceil(filtered.length / PER_PAGE)}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        color="primary"
                    />
                </Box>
            )}

            {/* 수정 다이얼로그 */}
            {editItem && (
                <Dialog open onClose={() => setEditItem(null)} maxWidth="sm" fullWidth>
                    <DialogTitle>가구 수정</DialogTitle>
                    <DialogContent dividers>
                        <Stack spacing={2} mt={1}>
                            <TextField label="이름" value={editItem.name || ""} onChange={e => setEditItem({ ...editItem, name: e.target.value })} />
                            <Button variant="outlined" component="label">이미지 업로드 교체
                                <input hidden type="file" accept="image/*" onChange={e => setNewImage(e.target.files[0])} />
                            </Button>
                            <Button variant="outlined" component="label">등(back) 이미지 업로드 교체
                                <input hidden type="file" accept="image/*" onChange={e => setNewBackImage(e.target.files[0])} />
                            </Button>
                            <Stack direction="row" spacing={2}>
                                <TextField type="number" label="size_w" value={editItem.size_w ?? 1} onChange={e => setEditItem({ ...editItem, size_w: e.target.value })} />
                                <TextField type="number" label="size_h" value={editItem.size_h ?? 1} onChange={e => setEditItem({ ...editItem, size_h: e.target.value })} />
                            </Stack>
                            <Stack direction="row" spacing={2}>
                                <TextField type="number" label="anchor_x" value={editItem.anchor_x ?? 0} onChange={e => setEditItem({ ...editItem, anchor_x: e.target.value })} />
                                <TextField type="number" label="anchor_y" value={editItem.anchor_y ?? 0} onChange={e => setEditItem({ ...editItem, anchor_y: e.target.value })} />
                            </Stack>
                            <TextField label="레이어" value={editItem.layer || "object"} onChange={e => setEditItem({ ...editItem, layer: e.target.value })} />
                            <TextField label="카테고리" value={editItem.category || ""} onChange={e => setEditItem({ ...editItem, category: e.target.value })} />
                            <FormControlLabel control={<Checkbox checked={!!editItem.rotatable} onChange={e => setEditItem({ ...editItem, rotatable: e.target.checked })} />} label="회전 가능" />
                            <FormControlLabel control={<Checkbox checked={!!editItem.is_active} onChange={e => setEditItem({ ...editItem, is_active: e.target.checked })} />} label="노출" />
                            <TextField
                                type="number"
                                label="가격(Leaf)"
                                value={editItem.price ?? 0}
                                onChange={e => setEditItem({ ...editItem, price: e.target.value })}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!editItem.is_unlimited}
                                        onChange={e => setEditItem({ ...editItem, is_unlimited: e.target.checked })}
                                    />
                                }
                                label="무제한 판매"
                            />
                            <TextField
                                type="number"
                                label="재고"
                                value={editItem.stock ?? 0}
                                onChange={e => setEditItem({ ...editItem, stock: e.target.value })}
                                disabled={!!editItem.is_unlimited}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditItem(null)}>취소</Button>
                        <Button variant="contained" onClick={saveEdit}>저장</Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}
